/* YouTube players are instantiated only by an explicit, keyboard-accessible click. */
class LkYouTubeVideo extends HTMLElement {
  connectedCallback() {
    if (this.ready) return;
    this.ready = true;
    this.querySelector('button')?.addEventListener('click', () => this.play(), { once: true });
  }

  play() {
    const iframe = this.querySelector('template')?.content.querySelector('iframe')?.cloneNode(true);
    if (!iframe) return;
    const url = new URL(iframe.getAttribute('src'), window.location.origin);
    if (url.hostname === 'www.youtube.com' || url.hostname === 'youtube.com') url.hostname = 'www.youtube-nocookie.com';
    url.searchParams.set('autoplay', '1');
    url.searchParams.set('playsinline', '1');
    iframe.src = url.href;
    iframe.loading = 'eager';
    iframe.title ||= 'YouTube-Video';
    iframe.allow = `${iframe.allow || ''}; autoplay; fullscreen`;
    this.replaceChildren(iframe);
    iframe.focus();
  }
}
if (!customElements.get('lk-youtube-video')) customElements.define('lk-youtube-video', LkYouTubeVideo);

/* The app's extension and legacy ScriptTag both wait for the actual review area. */
(() => {
  let loading;
  const loaded = new Set();
  const loadScript = (src) => new Promise((resolve, reject) => {
    if (loaded.has(src) || Array.from(document.scripts).some(script => script.src === src)) return resolve();
    const script = document.createElement('script');
    script.src = src;
    script.async = true;
    script.onload = () => { loaded.add(src); resolve(); };
    script.onerror = () => { script.remove(); reject(new Error('Review script unavailable')); };
    document.head.appendChild(script);
  });
  const loadReviews = () => {
    if (loading) return loading;
    const sources = Array.from(document.querySelectorAll('[data-lk-review-src]'), node => node.dataset.lkReviewSrc);
    const legacy = document.querySelector('[data-lk-review-legacy]');
    if (legacy) sources.push(JSON.parse(legacy.textContent));
    loading = (async () => {
      for (const src of new Set(sources)) await loadScript(src);
    })().catch(error => { loading = null; throw error; });
    return loading;
  };
  const activate = async (widget) => {
    try {
      if (widget.dataset.lkReviewUrl && !widget.dataset.lkReviewRendered) {
        const url = new URL(widget.dataset.lkReviewUrl, window.location.origin);
        const preview = new URLSearchParams(window.location.search).get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        const response = await fetch(url, { credentials: 'same-origin' });
        if (!response.ok) throw new Error('Review section unavailable');
        const template = document.createElement('template');
        template.innerHTML = await response.text();
        const reviews = template.content.querySelector('.reputon-google-reviews-widget');
        if (!reviews) throw new Error('Review widget missing');
        widget.replaceChildren((reviews.closest('.shopify-app-block') || reviews).cloneNode(true));
        template.content.querySelectorAll('script[src*="reputon-google"]').forEach(script => {
          const source = document.createElement('script');
          source.dataset.lkReviewSrc = script.src;
          document.head.appendChild(source);
        });
        widget.dataset.lkReviewRendered = 'true';
      }
      await loadReviews();
      widget.querySelector('.lk-reviews-retry')?.remove();
    } catch {
      if (widget.querySelector('.lk-reviews-retry')) return;
      const retry = document.createElement('button');
      retry.type = 'button';
      retry.className = 'lk-reviews-retry';
      retry.textContent = 'Bewertungen laden';
      retry.addEventListener('click', () => activate(widget));
      widget.appendChild(retry);
    }
  };
  const observe = (root = document) => {
    root.querySelectorAll('.lk-lazy-google-reviews, .reputon-google-reviews-widget').forEach(widget => {
      if (widget.dataset.lkObserved) return;
      widget.dataset.lkObserved = 'true';
      if ('IntersectionObserver' in window) {
        const observer = new IntersectionObserver(entries => {
          if (entries.some(entry => entry.isIntersecting)) {
            observer.disconnect();
            activate(widget);
          }
        }, { rootMargin: '600px 0px', threshold: 0 });
        observer.observe(widget);
      } else {
        const check = () => {
          const bounds = widget.getBoundingClientRect();
          if (bounds.top < window.innerHeight + 600 && bounds.bottom > -600) {
            window.removeEventListener('scroll', check);
            window.removeEventListener('resize', check);
            activate(widget);
          }
        };
        window.addEventListener('scroll', check, { passive: true });
        window.addEventListener('resize', check);
        check();
      }
    });
  };
  observe();
  document.addEventListener('shopify:section:load', event => observe(event.target));
})();
